import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SharedService } from '@ccfx/shared-lib';
import { Header } from '@ccfx/shared-lib/layout';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { RoleImpersonatorComponent } from './features/dev-tools/role-impersonator/role-impersonator';
import { NotificationBellComponent } from './features/notifications/components/notification-bell/notification-bell';
import { TourOverlayComponent } from './features/shared/components/tour-overlay/tour-overlay';
import { PermissionService } from './features/shared/services/permissions.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    Header,
    TranslocoPipe,
    RoleImpersonatorComponent,
    TourOverlayComponent,
    NotificationBellComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private readonly sharedService = inject(SharedService);
  private readonly permissions = inject(PermissionService);

  private readonly routeLabelSignals = new Map<string, Signal<string>>();

  private readonly currentRoute = signal<string>('');

  protected readonly title = signal('CCFX Wholesale Backend Portal');

  protected readonly showBackToHome = computed(() => {
    const route = this.currentRoute();
    return (
      !route.startsWith('/home') &&
      !route.startsWith('/login') &&
      !route.startsWith('/onboarding') &&
      !route.startsWith('/user-onboarding') &&
      route !== '/' &&
      route !== ''
    );
  });

  constructor() {
    this.permissions.NavcomponentMap.forEach((translationKey, route) => {
      this.routeLabelSignals.set(route, this.sharedService.getTranslation(translationKey));
    });
    this.routeLabelSignals.set('login', this.sharedService.getTranslation('login.title'));
    this.routeLabelSignals.set(
      'no-access',
      this.sharedService.getTranslation('noPermission.title'),
    );

    this.currentRoute.set(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.currentRoute.set(event.urlAfterRedirects));

    effect(() => {
      const baseTitle = this.title();
      const routeTitle = this.getRouteLabel(this.currentRoute());
      const pageTitle = routeTitle ? `${routeTitle} - ${baseTitle}` : baseTitle;

      this.titleService.setTitle(pageTitle);
    });
  }

  protected navigateHome(): void {
    this.router.navigate(['/home']);
  }

  private getRouteLabel(url: string): string | null {
    const path = url.split(/[?#]/)[0].replace(/^\/+/, '');
    if (!path) {
      return null;
    }

    const segment = path.split('/')[0];
    const translationSignal = this.routeLabelSignals.get(segment);

    return translationSignal ? translationSignal() : segment;
  }
}
